import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
	FormItem,
	Button,
	Space,
	Modal,
	ModalTitle,
	Textbox,
	IconButton,
	DayPickerInput,
	ValItem,
} from '@components';
import { cloneDeep } from 'lodash';
import {
	MeetingInfo, MeetingStatuses, ParticipantInfo, ParticipantRenameInfo, UpdateMeetingInfoRequest,
} from '@api';

import './EditMeetingInfoModal.scss';
import { GUID, makeTime } from '@common';
import { AddIcon } from '@icons';
import moment from 'moment';
import { editMeetingInfoValidator, ValMeetingInfo } from '@modals';

interface EditMeetingInfoModalProps {
	closeModal: () => void,
	meetingInfo: MeetingInfo,
	onSubmit: (updatedMeetingInfo: UpdateMeetingInfoRequest) => Promise<void>,
}

function EditMeetingInfoModal(props: EditMeetingInfoModalProps): JSX.Element {
	const {
		onSubmit,
		closeModal,
		meetingInfo,
	} = props;

	const [meetingSubject, setMeetingSubject] = useState<string>('');
	const [meetingParticipants, setMeetingParticipants] = useState<ParticipantInfo[]>([]);
	const [meetingStartDate, setMeetingStartDate] = useState(null as Date);
	const [meetingStartTime, setMeetingStartTime] = useState("");
	const [meetingEndTime, setMeetingEndTime] = useState("");
	const [meetingLocation, setMeetingLocation] = useState<string>('');
	const [valMessages, setValMessages] = useState({});
	
	useEffect(() => {
		setMeetingSubject(meetingInfo.subject);
		setMeetingParticipants(meetingInfo.participants);
		const startDate = moment(meetingInfo.startUtc).local();
		const endDate = moment(meetingInfo.endUtc).local();

		setMeetingStartDate(startDate.toDate());
		setMeetingStartTime(makeTime(startDate));
		setMeetingEndTime(makeTime(endDate));

		if (meetingInfo.locations != null && meetingInfo.locations.length) {
			setMeetingLocation(meetingInfo.locations[0].title);
		}

	}, [meetingInfo]);

	const handleChangeSubjectText = useCallback((e): void => {
		setMeetingSubject(e.target.value);
	}, [setMeetingSubject]);

	const handleChangeParticipantName = useCallback((id, e): void => {
		const updatedParticipants = cloneDeep(meetingParticipants);
		const updatedParticipant = updatedParticipants.find(p => p.id == id);
		updatedParticipant.name = e.target.value;

		setMeetingParticipants(updatedParticipants);

	}, [setMeetingParticipants, meetingParticipants]);

	const getFormData = useCallback(() => {
		const newMeetingInfo = new ValMeetingInfo();
		newMeetingInfo.id = GUID();
		newMeetingInfo.subject = meetingSubject;
		newMeetingInfo.location = meetingLocation;

		newMeetingInfo.startDateString = meetingStartDate;
		newMeetingInfo.startTimeString = meetingStartTime;
		newMeetingInfo.endTimeString = meetingEndTime;
		newMeetingInfo.startAndEndTimes = [meetingStartTime, meetingEndTime, meetingStartDate];
		newMeetingInfo.participants = meetingParticipants;

		return newMeetingInfo;
	}, [meetingSubject, meetingLocation, meetingStartDate, meetingStartTime, meetingEndTime, meetingParticipants]);

	const validateForm = useCallback((formData) => {
		let result = false;

		const callback = (errors, fields) => {
			if (errors) {
				setValMessages(fields);
				result = false;
			} else {
				setValMessages({});
				result = true;
			}
		};

		editMeetingInfoValidator.validate(formData, {}, callback);

		return result;
	}, []);

	const handleBlur = useCallback((): void => {
		const formData = getFormData();
		validateForm(formData);
	}, [getFormData, validateForm]);

	const handleSubmit = useCallback(async (): Promise<void> => {
		const formData = getFormData();
		const valResult = validateForm(formData);

		if (valResult) {

			const updatedParticipants = meetingParticipants.filter(mp => !meetingInfo.participants.some(p => p.id == mp.id)
				|| mp.name != meetingInfo.participants.find(p => mp.id == p.id).name);


			const updatedMeetingInfo = new UpdateMeetingInfoRequest();
			updatedMeetingInfo.subject = formData.subject;
			updatedMeetingInfo.updatedParticipants = updatedParticipants.map(p => new ParticipantRenameInfo(p));
			updatedMeetingInfo.location = formData.location;
			
			const startHours = formData.startTimeString.split(':')[0];
			const startMinutes = formData.startTimeString.split(':')[1];

			const endHours = formData.endTimeString.split(':')[0];
			const endMinutes = formData.endTimeString.split(':')[1];

			updatedMeetingInfo.startUtc = moment(formData.startDateString)
				.startOf('day')
				.add(startHours, 'hours')
				.add(startMinutes, 'minutes')
				.utc()
				.toDate();

				updatedMeetingInfo.endUtc = moment(formData.startDateString)
				.startOf('day')
				.add(endHours, 'hours')
				.add(endMinutes, 'minutes')
				.utc()
				.toDate();

			await onSubmit(updatedMeetingInfo);			
			closeModal();
		}

	}, [closeModal, meetingInfo, meetingParticipants, getFormData, validateForm, onSubmit]);

	const headerView = useMemo((): JSX.Element => {
		return (
			<div className="edit-meeting-info-modal__header">
				<ModalTitle onClose={closeModal}>
					Редактирование собрания
				</ModalTitle>
			</div>
		);
	}, [closeModal]);

	const footerView = useMemo(() => {
		return (
		<Space>
			<Button theme="primary" onClick={handleSubmit}>
				Сохранить
			</Button>
			<Button onClick={closeModal}>
				Отменить
			</Button>
			<div></div>
		</Space>
		);
	}, [handleSubmit, closeModal]);

	const participants = useMemo(() => {
		return meetingParticipants.map((p, index) => {
			const validationMessages = valMessages[`participants.${index}`];

			return <Space key={p.id}>
				<Textbox
					className="participant"
					key={p.id}
					value={p.name}
					onBlur={handleBlur}
					onChange={e => handleChangeParticipantName(p.id, e)}
				/>
				<ValItem validationMessages={validationMessages} />
			</Space>;
		});
	}, [meetingParticipants, valMessages, handleBlur, handleChangeParticipantName]);

	const handleAddClick = useCallback(() => {
		const participant = new ParticipantInfo({
			id: GUID(),
			name: `Участник ${meetingParticipants.length + 1}`,
		});
		const updatedParticipants = cloneDeep(meetingParticipants);
		updatedParticipants.push(participant);
		setMeetingParticipants(updatedParticipants);
	}, [meetingParticipants, setMeetingParticipants]);

	const handleChangeLocationText = useCallback((e): void => {
		setMeetingLocation(e.target.value);
	}, [setMeetingLocation]);

	const handleChangeStartDate = useCallback((date): void => {
		setMeetingStartDate(date);
	}, [setMeetingStartDate]);

	const handleChangeStartTime = useCallback((e): void => {
		setMeetingStartTime(e.target.value);
	}, [setMeetingStartTime]);

	const handleChangeEndTime = useCallback((e): void => {
		setMeetingEndTime(e.target.value);
	}, [setMeetingEndTime]);

	const isPlannedRecording = meetingInfo.status.alias == MeetingStatuses.Plan;

	return (meetingInfo &&
		<Modal
			className="edit-meeting-info-modal"
			header={headerView}
			footer={footerView}
			isOpen
		>
			<div className="edit-meeting-info-modal__content">
				<FormItem label="Название" validationMessages={valMessages['subject']}>
					<Textbox
						value={meetingSubject}
						onChange={handleChangeSubjectText}
						onBlur={handleBlur}
					/>
				</FormItem>
				<Space>
					<FormItem label="Дата проведения" validationMessages={valMessages['startDateString']}>
						<DayPickerInput
							value={meetingStartDate}
							onDayChange={handleChangeStartDate}
							onBlur={handleBlur}
						/>
					</FormItem>
					<FormItem label="Время проведения" validationMessages={valMessages['startAndEndTimes']}>
						<Space>
							<Textbox
								type='time'
								value={meetingStartTime}
								onChange={handleChangeStartTime}
								className="time"
								onBlur={handleBlur}
							/>
							<div className="time-interval">—</div>
							<Textbox
								type='time'
								value={meetingEndTime}
								onChange={handleChangeEndTime}
								className="time"
								onBlur={handleBlur}
							/>
						</Space>
					</FormItem>
				</Space>
				{!isPlannedRecording ?
					<FormItem label="Расположение" validationMessages={valMessages['location']}>
						<Textbox
							value={meetingLocation}
							onChange={handleChangeLocationText}
							onBlur={handleBlur}
						/>
					</FormItem>
				: null }
				<FormItem label="Список участников">
					<>
					<div className="participants">{participants}</div>
					<IconButton
						theme="ghost"
						icon={<AddIcon />}
						iconSize="20px"
						onClick={handleAddClick}
					>
						Добавить участника
					</IconButton>
					</>
				</FormItem>
			</div>
		</Modal>
	);
}

export {EditMeetingInfoModal};